/** Customer presentation only. Never use these totals to post shop/Hub ledger entries. */
export interface CustomerOrderPricingInput {
  items?: ReadonlyArray<{ pricePence: number; quantity: number; lineTotalPence?: number | null; isFreeItem?: boolean | null; shopDiscountPence?: number | null; hubPromoPence?: number | null; customerPrePromoPence?: number | null }>;
  subtotalPence?: number | null;
  customerUpliftMarginPence?: number | null;
  offerDiscountPence?: number | null;
  platformFundedPromoPence?: number | null;
  offerTitle?: string | null;
  deliveryFeePence?: number | null;
  leg2DeliveryFeePence?: number | null;
  serviceChargePence?: number | null;
  bagChargePence?: number | null;
  customerPlatformFeePence?: number | null;
  customerHubDriverFeePence?: number | null;
  kindnessContributionPence?: number | null;
  kindnessCreditPence?: number | null;
  customerPaidPence?: number | null;
  totalPence?: number | null;
  isMultiOrderChild?: boolean | null;
}
export interface CustomerPricingRow { key: string; label: string; pence: number; }
const amount = (v: number | null | undefined) => Number.isFinite(v) ? Math.max(0, Math.round(v!)) : 0;
export function customerItemTotal(item: NonNullable<CustomerOrderPricingInput["items"]>[number]): number {
  if (item.isFreeItem) return 0;
  // New immutable snapshots store post-promotion line totals. Older orders store
  // gross lines with these fields NULL. Restore only explicitly recorded shares.
  const savedLine = amount(item.lineTotalPence ?? item.pricePence * item.quantity);
  return item.customerPrePromoPence != null
    ? savedLine + amount(item.shopDiscountPence) + amount(item.hubPromoPence)
    : savedLine;
}
/** Gross snapshot prices, full saving (including discounted uplift), and the exact original payment.
 * Hub-funded base codes are already in offerDiscountPence: adding platformFundedPromoPence
 * directly would count those twice. Refunds and later driver tips never change this receipt.
 */
export function customerOrderPricing(order: CustomerOrderPricingInput) {
  const base = amount(order.subtotalPence), netUplift = amount(order.customerUpliftMarginPence);
  const hasItems = !!order.items?.length;
  const itemsPence = hasItems ? order.items!.reduce((sum, item) => sum + customerItemTotal(item), 0) : base + netUplift;
  const baseDiscount = amount(order.offerDiscountPence);
  const upliftReduction = Math.max(0, itemsPence - base - netUplift);
  const upliftDiscount = (baseDiscount > 0 || amount(order.platformFundedPromoPence) > 0)
    ? Math.min(upliftReduction, order.platformFundedPromoPence == null ? upliftReduction : amount(order.platformFundedPromoPence)) : 0;
  const discountPence = Math.min(itemsPence, baseDiscount + upliftDiscount);
  const rows: CustomerPricingRow[] = [{ key: "items", label: "Items subtotal", pence: itemsPence }];
  if (discountPence > 0) rows.push({ key: "discount", label: order.offerTitle || "Promotion", pence: -discountPence });
  const charges: Array<[string, string, number]> = [
    ["delivery", "Delivery", amount(order.deliveryFeePence)],
    ["returnDelivery", "Return delivery", amount(order.leg2DeliveryFeePence)],
    ["shopService", "Shop service charge", amount(order.serviceChargePence)],
    ["bags", "Carrier bag charge", amount(order.bagChargePence)],
    ["service", "Service fee", amount(order.customerPlatformFeePence) + amount(order.customerHubDriverFeePence)],
    ["kindnessContribution", "Pay it forward", amount(order.kindnessContributionPence)],
    ["kindnessCredit", "Kindness credit", -amount(order.kindnessCreditPence)],
  ];
  for (const [key, label, pence] of charges) if (pence !== 0) rows.push({ key, label, pence });
  const calculatedTotalPence = rows.reduce((sum, row) => sum + row.pence, 0);
  // Legacy multi-shop children can carry a group-level payment snapshot. Their receipt
  // uses child components; delivery/payment for the group is shown once by its parent.
  const paidPence = order.isMultiOrderChild ? calculatedTotalPence
    : order.customerPaidPence != null ? amount(order.customerPaidPence)
    : order.totalPence != null ? amount(order.totalPence) + netUplift + amount(order.customerPlatformFeePence) + amount(order.customerHubDriverFeePence) + amount(order.kindnessContributionPence) - amount(order.kindnessCreditPence)
    : calculatedTotalPence;
  const adjustment = paidPence - calculatedTotalPence;
  if (adjustment !== 0) rows.push({ key: "adjustment", label: "Order adjustment", pence: adjustment });
  return { itemsPence, discountPence, paidPence, rows };
}
